import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from "../components/ui/card";
import { Monitor, FileText, MapPin, CheckCircle2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";

export default function Examinari() {
  const [searchParams, setSearchParams] = useSearchParams();
  const openModalId = searchParams.get('modal');

  const examinariOptions = [
    {
      id: 'online',
      title: 'Examinare pe calculator on-line',
      desc: 'Informații privind susținerea probei teoretice (sala) pe calculator.',
      docs: [
        'Activitatea se desfășoară la sediul S.P.C.R.P.C.I.V. Gorj',
        'Zile de examinare: Luni - Joi (08:30 - 15:30), Vineri (08:30 - 12:30)',
        'Candidatul se prezintă cu dosarul personal complet',
        'Persoanele declarate "RESPINS" pot susține un nou examen după cel puțin 15 zile',
        'Achiziția imaginii pentru permis se face în aceeași zi, dacă candidatul a promovat'
      ]
    },
    {
      id: 'acte',
      title: 'Acte necesare',
      desc: 'Conținutul dosarului de examinare pe care candidatul trebuie să-l prezinte.',
      docs: [
        'Cererea tip, semnată de candidat',
        'Fișa de școlarizare emisă de școala de șoferi',
        'Certificatul de cazier judiciar valabil (6 luni)',
        'Actul de identitate (original și copie)',
        'Chitanța de plată a taxei de permis (89 lei)'
      ]
    },
    {
      id: 'sali',
      title: 'Săli de examinare',
      desc: 'Locațiile unde se susțin probele teoretice și practice.',
      docs: [
        'Proba teoretică: Mun. Tg-Jiu, Str. Victoriei nr. 2-4',
        'Plecarea pentru proba practică: Din zonele special amenajate și comunicate de către examinatori',
        'Este obligatorie prezența cu minim 15 minute înainte de ora programată'
      ]
    }
  ];

  const handleOpenChange = (id, isOpen) => {
    if (isOpen) {
      setSearchParams({ modal: id }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <h1 className="text-3xl font-bold text-slate-800 border-b pb-4">Examinări</h1>
      
      <p className="text-slate-600 text-lg mb-6">
        Pentru programarea la examen în vederea obținerii permisului de conducere, selectați opțiunea de interes pentru mai multe detalii:
      </p>

      <div className="grid md:grid-cols-3 gap-4 mb-10">
        {examinariOptions.map((opt) => (
          <Dialog key={opt.id} open={openModalId === opt.id} onOpenChange={(isOpen) => handleOpenChange(opt.id, isOpen)}>
            <DialogTrigger asChild>
              <div className={`border rounded-lg p-5 shadow-sm transition-all cursor-pointer group text-left flex flex-col ${openModalId === opt.id ? 'bg-blue-50 border-blue-400' : 'bg-white hover:border-blue-400 hover:bg-blue-50/50'}`}>
                {opt.id === 'online' && <Monitor className="w-6 h-6 text-blue-600 mb-3" />}
                {opt.id === 'acte' && <FileText className="w-6 h-6 text-blue-600 mb-3" />}
                {opt.id === 'sali' && <MapPin className="w-6 h-6 text-blue-600 mb-3" />}
                <h4 className="font-semibold text-blue-700 mb-2 group-hover:text-blue-800 text-lg">{opt.title}</h4>
                <p className="text-sm text-slate-500">{opt.desc}</p>
              </div>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md bg-white">
              <DialogHeader>
                <DialogTitle className="text-xl text-blue-900">{opt.title}</DialogTitle>
                <DialogDescription className="text-slate-500 mt-2">
                  Informații esențiale:
                </DialogDescription>
              </DialogHeader>
              <div className="mt-4">
                <ul className="space-y-3 m-0 list-none p-0">
                  {opt.docs.map((doc, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700 text-sm font-medium">{doc}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </DialogContent>
          </Dialog>
        ))}
      </div>

      <h3 className="text-xl font-semibold text-slate-800 mt-8 mb-4 border-t pt-6 border-slate-100">Structura probei teoretice pe categorii:</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        <Card className="bg-slate-50 border-none shadow-sm">
          <CardContent className="p-4 flex gap-4 items-start">
            <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0 font-bold">A</div>
            <div>
              <h4 className="font-semibold text-slate-800">Categoriile A, A1, A2, AM</h4>
              <p className="text-sm text-slate-500 mt-1">20 de întrebări, timp alocat: 20 minute. Se admit maxim 3 greșeli (17 puncte pentru promovare).</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-50 border-none shadow-sm">
          <CardContent className="p-4 flex gap-4 items-start">
            <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0 font-bold">B</div>
            <div>
              <h4 className="font-semibold text-slate-800">Categoriile B, B1</h4>
              <p className="text-sm text-slate-500 mt-1">26 de întrebări, timp alocat: 30 minute. Se admit maxim 4 greșeli (22 puncte pentru promovare).</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-50 border-none shadow-sm">
          <CardContent className="p-4 flex gap-4 items-start">
            <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0 font-bold">C</div>
            <div>
              <h4 className="font-semibold text-slate-800">Categoriile C, C1, Tr</h4>
              <p className="text-sm text-slate-500 mt-1">26 de întrebări, timp alocat: 30 minute. Se admit maxim 4 greșeli (22 puncte pentru promovare).</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-50 border-none shadow-sm">
          <CardContent className="p-4 flex gap-4 items-start">
            <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0 font-bold">D</div>
            <div>
              <h4 className="font-semibold text-slate-800">Categoriile D, D1, Tb, Tv</h4>
              <p className="text-sm text-slate-500 mt-1">26 de întrebări, timp alocat: 30 minute. Se admit maxim 4 greșeli (22 puncte pentru promovare).</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
